import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { AuthPageLayout } from './AuthPageLayout';
import { Form, FormInput, FormItemGroup, FormLabel, FormError, SubmitButton } from './components/Form';

interface ResetPasswordFormData {
  password: string;
  confirmPassword: string;
}

export function PasswordResetPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm<ResetPasswordFormData>();

  const password = watch('password');

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!token) {
      setError('Invalid reset token');
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          token,
          password: data.password 
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to reset password');
      }

      setSuccess(true);
      reset();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthPageLayout>
        <div className="text-center">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Invalid reset link
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            This password reset link is invalid or has expired.
          </p>
          <Link
            to="/request-password-reset"
            className="text-sm font-medium text-blue-600 hover:text-blue-500"
          >
            Request a new reset link
          </Link>
        </div>
      </AuthPageLayout>
    );
  }

  if (success) {
    return (
      <AuthPageLayout>
        <div className="text-center">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Password reset successful
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Your password has been successfully reset. You can now log in with your new password.
          </p>
          <Link
            to="/login"
            className="text-sm font-medium text-blue-600 hover:text-blue-500"
          >
            Go to login
          </Link>
        </div>
      </AuthPageLayout>
    );
  }

  return (
    <AuthPageLayout>
      <div className="text-center mb-6">
        <h2 className="text-lg font-semibold text-gray-900">
          Set new password
        </h2>
        <p className="text-sm text-gray-600 mt-2">
          Enter your new password below.
        </p>
      </div>
      
      <Form onSubmit={handleSubmit(onSubmit)}>
        <FormItemGroup>
          <FormLabel>New Password</FormLabel>
          <FormInput
            {...register('password', {
              required: 'Password is required',
              minLength: {
                value: 6,
                message: 'Password must be at least 6 characters',
              },
            })}
            type="password"
            disabled={isLoading}
            placeholder="Enter your new password"
          />
          {errors.password && <FormError>{errors.password.message}</FormError>}
        </FormItemGroup>
        
        <FormItemGroup>
          <FormLabel>Confirm New Password</FormLabel>
          <FormInput
            {...register('confirmPassword', {
              required: 'Please confirm your password',
              validate: (value) => value === password || 'Passwords do not match',
            })}
            type="password"
            disabled={isLoading}
            placeholder="Confirm your new password"
          />
          {errors.confirmPassword && <FormError>{errors.confirmPassword.message}</FormError>}
        </FormItemGroup>
        
        {error && <FormError>{error}</FormError>}
        
        <FormItemGroup>
          <SubmitButton type="submit" disabled={isLoading}>
            {isLoading ? 'Resetting...' : 'Reset password'}
          </SubmitButton>
        </FormItemGroup>
      </Form>
      
      <div className="mt-4 text-center">
        <Link
          to="/login"
          className="text-sm font-medium text-blue-600 hover:text-blue-500"
        >
          Back to login
        </Link>
      </div>
    </AuthPageLayout>
  );
}
